import { createTRPCProxyClient, httpBatchLink, TRPCClientError } from '@trpc/client';
import posthog from 'posthog-js';
import type {
  AppendEntryInput,
  AppendEntryOutput,
  CloseThreadInput,
  FeedbackSetInput,
  GetThreadInput,
  GetThreadOutput,
  ListThreadsInput,
  ListThreadsOutput,
  AssistantRun,
  CancelAssistantRunInput,
  GetAssistantRunInput,
  GetActiveAssistantRunInput,
  StartAssistantRunInput,
  AssistantClientCapability,
} from '@charitystack/dashboard-assistant-contracts';
import { DASHBOARD_ASSISTANT_CLIENT_CAPABILITIES } from '@charitystack/dashboard-assistant-contracts';
import { getAuthToken } from '../utils/auth';

const DASHBOARD_ASSISTANT_HISTORY_URL =
  import.meta.env.VITE_APP_DASHBOARD_ASSISTANT_TRPC_URL ||
  import.meta.env.VITE_DASHBOARD_ASSISTANT_TRPC_URL ||
  '';

// Keep this untyped here so the dashboard package does not import backend-only router modules.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cachedClient: any = null;

function normalizeBaseUrl(url: string) {
  return url.replace(/\/$/, '');
}

export function isDashboardAssistantHistoryEnabled() {
  return Boolean(DASHBOARD_ASSISTANT_HISTORY_URL);
}

function getDashboardAssistantHistoryClient() {
  const url = normalizeBaseUrl(DASHBOARD_ASSISTANT_HISTORY_URL);
  if (!url) {
    throw new Error('Dashboard assistant history backend is not configured.');
  }

  if (!cachedClient) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cachedClient = createTRPCProxyClient<any>({
      links: [
        httpBatchLink({
          url,
          async headers() {
            const token = await getAuthToken();
            const posthogDistinctId = posthog?.get_distinct_id?.();
            const posthogSessionId = posthog?.get_session_id?.();
            return {
              ...(token ? { Authorization: token } : {}),
              ...(posthogDistinctId ? { 'x-posthog-distinct-id': posthogDistinctId } : {}),
              ...(posthogSessionId ? { 'x-posthog-session-id': posthogSessionId } : {}),
            };
          },
          // Custom fetch to disable HTTP-level caching. TRPC sends queries as
          // GET by default; without `cache: 'no-store'` the browser happily
          // serves stale responses, which broke continuous polling of the
          // assistant thread (the latest assistant entry never appeared in
          // the UI even though DynamoDB had it).
          fetch(input, init) {
            return fetch(input, { ...init, cache: 'no-store' });
          },
        }),
      ],
    });
  }

  return cachedClient;
}

export async function listAssistantThreads(input: ListThreadsInput) {
  return getDashboardAssistantHistoryClient().threads.list.query(
    input,
  ) as Promise<ListThreadsOutput>;
}

/**
 * Reads a chat's history, saying what this tab can render: saved proposal
 * cards drop fields it didn't declare (Greptile P1 on #4702), so a tab from
 * before ADM-228 reading the same chat never gets `ruleChange`. Same fallback
 * as the run start for a backend that rejects a capability.
 */
export async function getAssistantThread(input: GetThreadInput) {
  const client = getDashboardAssistantHistoryClient();
  return withCapabilityFallback(
    input.clientCapabilities ?? DASHBOARD_ASSISTANT_CLIENT_CAPABILITIES,
    (clientCapabilities) => {
      const { clientCapabilities: _declared, ...rest } = input;
      return client.threads.get.query(clientCapabilities ? { ...rest, clientCapabilities } : rest) as Promise<GetThreadOutput>;
    },
  );
}

export async function appendAssistantEntry(input: AppendEntryInput) {
  return getDashboardAssistantHistoryClient().entries.append.mutate(
    input,
  ) as Promise<AppendEntryOutput>;
}

type StartedAssistantRun = {
  run: AssistantRun;
  thread: GetThreadOutput['thread'];
  entry: GetThreadOutput['entries'][number];
  idempotentReplay: boolean;
};

// The capability set an assistant backend from before ADM-228 accepts (ADM-223).
const PREVIOUS_CLIENT_CAPABILITIES: readonly AssistantClientCapability[] = ['keepSavedPeople'];

/**
 * Sends a request with the tab's capabilities. An older assistant backend
 * rejects a capability it doesn't know as BAD_REQUEST before it writes
 * anything (the run context schema is strict). So the request is sent again
 * with the previous capability set (ADM-228: a backend from before ruleChange
 * still gets keepSavedPeople), and only if that is rejected too, with no
 * capabilities (a backend from before ADM-223). Any other failure is never
 * retried.
 */
async function withCapabilityFallback<T>(
  capabilities: readonly AssistantClientCapability[] | undefined,
  send: (capabilities: AssistantClientCapability[] | undefined) => Promise<T>,
): Promise<T> {
  const attempts: Array<AssistantClientCapability[] | undefined> = [capabilities ? [...capabilities] : undefined];
  if (capabilities) {
    const previous = capabilities.filter((capability) => PREVIOUS_CLIENT_CAPABILITIES.includes(capability));
    if (previous.length > 0 && previous.length < capabilities.length) attempts.push(previous);
    attempts.push(undefined);
  }
  for (let index = 0; ; index += 1) {
    try {
      return await send(attempts[index]);
    } catch (error) {
      const rejectedInput = error instanceof TRPCClientError && (error.data as { code?: string } | undefined)?.code === 'BAD_REQUEST';
      if (!rejectedInput || index === attempts.length - 1) throw error;
    }
  }
}

/** Starts a run, with the tab's capabilities in its context (see withCapabilityFallback). */
export async function startAssistantRun(input: StartAssistantRunInput): Promise<StartedAssistantRun> {
  const client = getDashboardAssistantHistoryClient();
  const context = input.context;
  if (!context?.clientCapabilities) return client.runs.start.mutate(input);
  const { clientCapabilities, ...rest } = context;
  return withCapabilityFallback(clientCapabilities, (capabilities) =>
    client.runs.start.mutate({ ...input, context: capabilities ? { ...rest, clientCapabilities: capabilities } : rest }));
}

export async function getAssistantRun(input: GetAssistantRunInput) {
  return getDashboardAssistantHistoryClient().runs.get.query(input) as Promise<AssistantRun>;
}

export async function getActiveAssistantRun(input: GetActiveAssistantRunInput) {
  return getDashboardAssistantHistoryClient().runs.active.query(
    input,
  ) as Promise<AssistantRun | null>;
}

export async function cancelAssistantRun(input: CancelAssistantRunInput) {
  return getDashboardAssistantHistoryClient().runs.cancel.mutate(input) as Promise<AssistantRun>;
}

export async function closeAssistantThread(input: CloseThreadInput) {
  return getDashboardAssistantHistoryClient().threads.close.mutate(input);
}

export async function setAssistantFeedback(input: FeedbackSetInput) {
  return getDashboardAssistantHistoryClient().feedback.set.mutate(input) as Promise<{
    ok: boolean;
  }>;
}

// AI Tag Automation (initiatives/ai-tag-automation) — apply path. Pulls one
// frozen page of contact ids from a materialized selection (by selectionToken),
// org-scoped + MANAGE_CONTACTS-gated server-side. The PreviewCard loops this
// (cursor → done) and writes each page via POST /contacts/tags.
export type ContactSelectionChunk = {
  contactIds: string[];
  nextCursor: number;
  done: boolean;
  total: number;
  matchedCount: number;
  criteriaEcho: string;
  exclusionRevision: number;
};

export async function getContactSelectionChunk(input: {
  organizationId: string;
  threadId: string;
  token: string;
  expectedExclusionRevision: number;
  cursor: number;
  limit?: number;
}) {
  return getDashboardAssistantHistoryClient().contactSelection.getChunk.query(
    input,
  ) as Promise<ContactSelectionChunk>;
}

// Display rows (name/email/phone/location) for the PreviewCard's full
// reviewable table, paged from the server-materialized selection.
export type ContactSelectionRow = {
  id: string;
  fullName?: string;
  email?: string;
  phone?: string;
  // "city, region, country" of the geo-matched (else primary) address.
  location?: string;
};

export type ContactSelectionRowsPage = {
  rows: ContactSelectionRow[];
  nextCursor: number;
  done: boolean;
  total: number;
  matchedCount: number;
  exclusionRevision: number;
  excludedContactIds: string[];
};

export async function getContactSelectionRows(input: {
  organizationId: string;
  threadId: string;
  token: string;
  cursor: number;
  limit?: number;
}) {
  return getDashboardAssistantHistoryClient().contactSelection.getRows.query(
    input,
  ) as Promise<ContactSelectionRowsPage>;
}

// Sync the PreviewCard's unchecked rows server-side so a chat-driven apply
// (the assistant's apply_contact_tags tool) honors the same exclusions as the
// card's own Apply button. Full replace each call.
export async function setContactSelectionExclusions(input: {
  organizationId: string;
  threadId: string;
  token: string;
  expectedRevision: number;
  excludedContactIds: string[];
}) {
  return getDashboardAssistantHistoryClient().contactSelection.setExclusions.mutate(
    input,
  ) as Promise<
    | { ok: true; revision: number; excludedContactIds: string[] }
    | { ok: false; conflict: true; revision: number; excludedContactIds: string[] }
  >;
}
